import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { logoutApi, meApi } from './api';
import { onUnauthorized } from '@/lib/api-client';
import type { PublicUser } from './types';

type Status = 'loading' | 'authed' | 'guest';

interface AuthContextValue {
  user: PublicUser | null;
  status: Status;
  /** Inicia o fluxo Google: navega o browser pra URL de auth retornada pelo backend. */
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: PublicUser | null) => void;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  const refreshMe = useCallback(async () => {
    try {
      const u = await meApi();
      setUser(u);
      setStatus('authed');
    } catch {
      setUser(null);
      setStatus('guest');
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    const unsub = onUnauthorized(() => {
      setUser(null);
      setStatus('guest');
    });
    return () => unsub();
  }, []);

  const loginWithGoogle = useCallback(async () => {
    // Navegação top-level direta pro backend: ele seta os cookies temp de
    // OAuth em contexto first-party e responde com 302 pro Google. Evita
    // problema de cookies cross-site quando frontend e API estão em sites
    // diferentes (ex: *.onrender.com está no Public Suffix List).
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';
    window.location.href = `${apiBase}/auth/google/start`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } finally {
      setUser(null);
      setStatus('guest');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, loginWithGoogle, logout, setUser, refreshMe }),
    [user, status, loginWithGoogle, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
